import { Column } from '@tanstack/react-table'
import {
  ArrowDown,
  ArrowDownUp,
  ArrowRight,
  ArrowUp,
  Globe,
  Plus,
  TrashIcon,
} from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useOrgsApi } from 'src/api/db/apiOrgs'
import { useQuoteApi } from 'src/api/db/apiQuote'
import { Row } from 'src/shared/components/Semantic/all'
import { Badge } from 'src/shared/components/ui/badge'
import { Button } from 'src/shared/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'src/shared/components/ui/dropdown-menu'
import { paths } from 'src/routes/paths'
import { DeleteConfirmDialog } from 'src/shared/components/OrderTable/components/shared/DeleteConfirmDialog'
import { useLocalization } from 'src/shared/hooks/useLocalization'

import { ClientMember } from './consts'

const SortableHeader = ({
  column,
  children,
}: {
  column: Column<ClientMember, unknown>
  children: any
}) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <Row
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="cursor-pointer items-center"
        >
          {children}
          {column.getIsSorted() === 'asc' ? (
            <ArrowUp className="ml-2 h-4 w-4" />
          ) : column.getIsSorted() === 'desc' ? (
            <ArrowDown className="ml-2 h-4 w-4" />
          ) : (
            <ArrowDownUp className="ml-2 h-4 w-4" />
          )}
        </Row>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start">
        <DropdownMenuItem onClick={() => column.toggleSorting(false)}>
          Sort Ascending
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => column.toggleSorting(true)}>
          Sort Descending
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export const useColumns = () => {
  const { deleteOrganization } = useOrgsApi({})
  const { createQuote } = useQuoteApi({ skipListQuery: true })
  const navigate = useNavigate()
  const { t } = useLocalization()
  const [organizationToDelete, setOrganizationToDelete] = useState<{
    id: string
    name: string
  } | null>(null)

  const handleCreateQuote = async (orgId: string) => {
    try {
      const res = await createQuote.mutateAsync({
        name: 'default',
        org_id: orgId,
      })

      if (res?.id) {
        navigate(`${paths.admin}${paths.adminOrders}/${res.public_id}`)
      }
    } catch (error) {
      console.error('Failed to create quote:', error)
    }
  }

  return [
    {
      accessorKey: 'name',
      header: ({ column }) => (
        <SortableHeader column={column}>Name</SortableHeader>
      ),
      cell: ({ row }) => (
        <Button
          size={'sm'}
          variant={'ghost'}
          onClick={() =>
            navigate(`${paths.adminOrganizations}/${row.original.id}`)
          }
        >
          {row.getValue('name')}
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      ),
    },
    {
      accessorKey: 'source',
      header: ({ column }) => (
        <SortableHeader column={column}>Source</SortableHeader>
      ),
      cell: ({ row }) => {
        const source = row.getValue('source') as string | null | undefined
        if (source == null || source === '') {
          return <Badge variant="secondary">N/A</Badge>
        }
        return (
          <Badge variant="secondary">
            {source === 'external' && (
              <Globe className="h-4 w-4 mr-2 text-blue-500" />
            )}
            {source.charAt(0).toUpperCase() + source.slice(1)}
          </Badge>
        )
      },
    },
    {
      id: 'see_quotes',
      header: 'Orders',
      cell: ({ row }) => {
        const orgId = row.original.id
        return (
          <Row className="gap-2">
            <Button
              onClick={() => handleCreateQuote(orgId)}
              title="Add order"
              size={'sm'}
              variant={'ghost'}
              disabled={createQuote.isPending}
            >
              <Plus className="h-4 w-4 mr-2" />
              New order
            </Button>
          </Row>
        )
      },
    },
    {
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => {
        const org = row.original
        return (
          <>
            <Button
              size={'icon'}
              variant={'ghost'}
              onClick={() =>
                setOrganizationToDelete({ id: org.id, name: org.name })
              }
            >
              <TrashIcon className="h-4 w-4 text-red-500" />
            </Button>

            <DeleteConfirmDialog
              open={organizationToDelete?.id === org.id}
              onOpenChange={(open) => !open && setOrganizationToDelete(null)}
              title={t('Delete organization', 'Ta bort organisation')}
              description={`${t(
                'Are you sure you want to delete',
                'Är du säker på att du vill ta bort'
              )} "${org.name}"? ${t(
                'This action cannot be undone.',
                'Denna åtgärd kan inte ångras.'
              )}`}
              onConfirm={() =>
                deleteOrganization.mutate(org.id, {
                  onSettled: () => setOrganizationToDelete(null),
                })
              }
            />
          </>
        )
      },
    },
    {
      accessorKey: 'created_at',
      header: ({ column }) => (
        <SortableHeader column={column}>Created at</SortableHeader>
      ),
      cell: ({ row }) => {
        const createdAt = row.getValue('created_at') as string | undefined
        if (!createdAt) return <Badge variant="secondary">N/A</Badge>
        const date = new Date(createdAt)
        const dateStr = date.toLocaleDateString()
        const timeStr = date.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        })
        return (
          <Badge variant="secondary">
            {dateStr} • {timeStr}
          </Badge>
        )
      },
    },
  ]
}
